import { useState, useRef, useEffect, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import { getProductPages, type ProductSpecItem } from '@/api/product';

export interface SpecFilterOption {
  id: string;
  label: string;
}

interface SpecFilterSelectProps {
  value: SpecFilterOption[];
  onChange: (value: SpecFilterOption[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
}

function mapOption(item: ProductSpecItem): SpecFilterOption {
  const goodsName = String(item.goods?.title || '未命名商品').trim();
  const specName = String(item.name || '').trim();
  return {
    id: String(item.id),
    label: specName ? `${goodsName} / ${specName}` : goodsName,
  };
}

export default function SpecFilterSelect({
  value,
  onChange,
  placeholder = '搜索 / 选择规格',
  searchPlaceholder = '继续搜索规格...',
}: SpecFilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<SpecFilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqGenRef = useRef(0);

  const selectedIds = new Set(value.map((v) => v.id));

  const fetchOptions = useCallback(async (keyword: string) => {
    const gen = ++reqGenRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await getProductPages({
        page: 1,
        pagenumber: 30,
        search: keyword.trim() || undefined,
      });
      if (gen !== reqGenRef.current) return;

      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setError(res.message || res.msg || '规格加载失败');
        setOptions([]);
        return;
      }
      const list = Array.isArray(res.data) ? res.data.map(mapOption) : [];
      setOptions(list);
    } catch {
      if (gen !== reqGenRef.current) return;
      setError('规格加载失败');
      setOptions([]);
    } finally {
      if (gen === reqGenRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchOptions(search);
    }, search ? 280 : 0);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, search, fetchOptions]);

  const toggle = (opt: SpecFilterOption) => {
    if (selectedIds.has(opt.id)) {
      onChange(value.filter((v) => v.id !== opt.id));
    } else {
      onChange([...value, opt]);
    }
    // 保留当前搜索词与结果列表，便于连续多选
    inputRef.current?.focus();
  };

  const clearAll = (e?: ReactMouseEvent) => {
    e?.stopPropagation();
    onChange([]);
    setSearch('');
    inputRef.current?.focus();
  };

  const removeOne = (id: string, e: ReactMouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v.id !== id));
  };

  const openDropdown = () => {
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const inputPlaceholder = value.length === 0 ? placeholder : searchPlaceholder;

  return (
    <div ref={containerRef} className="relative">
      <div
        role="combobox"
        aria-expanded={open}
        tabIndex={-1}
        onClick={openDropdown}
        className={`flex items-center gap-1.5 px-2.5 h-[38px] text-sm border rounded-lg bg-white transition-colors cursor-text w-full min-w-0 overflow-hidden ${
          open ? 'border-primary-400' : 'border-background-200 hover:border-primary-400'
        }`}
      >
        <i className="ri-box-3-line text-foreground-400 text-xs shrink-0" />

        <div className="flex-1 min-w-0 flex items-center gap-1 flex-nowrap overflow-hidden">
          {value.length > 1 ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary-50 text-primary-700 border border-primary-100 whitespace-nowrap shrink-0">
              已选 {value.length} 个规格
            </span>
          ) : value.length === 1 ? (
            <span className="inline-flex items-center gap-0.5 max-w-[55%] px-1.5 py-0.5 rounded text-xs bg-primary-50 text-primary-700 border border-primary-100 shrink min-w-0">
              <span className="truncate">{value[0].label}</span>
              <button
                type="button"
                onClick={(e) => removeOne(value[0].id, e)}
                className="shrink-0 hover:text-primary-900 leading-none"
                aria-label={`移除 ${value[0].label}`}
              >
                <i className="ri-close-line text-[11px]" />
              </button>
            </span>
          ) : null}

          <input
            ref={inputRef}
            type="text"
            value={search}
            placeholder={inputPlaceholder}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !search && value.length > 0) {
                onChange(value.slice(0, -1));
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setSearch('');
                inputRef.current?.blur();
              }
            }}
            className="min-w-[4rem] flex-1 outline-none bg-transparent text-sm py-0.5 placeholder:text-foreground-400 truncate"
          />
        </div>

        <div className="w-4 h-4 shrink-0 flex items-center justify-center">
          {value.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-foreground-300 hover:text-foreground-500 leading-none"
              aria-label="清空"
            >
              <i className="ri-close-circle-fill text-sm" />
            </button>
          )}
        </div>

        <i
          className={`ri-arrow-down-s-line text-xs text-foreground-400 shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </div>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-full min-w-[260px] bg-white border border-background-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="h-56 overflow-y-auto">
            <button
              type="button"
              onClick={() => clearAll()}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-background-50 transition-colors ${
                value.length === 0
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-foreground-700'
              }`}
            >
              全部规格
            </button>

            {loading && (
              <div className="px-3 py-3 text-xs text-foreground-400 flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin" />
                搜索中...
              </div>
            )}

            {!loading && error && (
              <div className="px-3 py-3 text-xs text-primary-600">{error}</div>
            )}

            {!loading && !error && options.length === 0 && (
              <div className="px-3 py-3 text-xs text-foreground-400 text-center">无匹配规格</div>
            )}

            {!loading &&
              options.map((opt) => {
                const checked = selectedIds.has(opt.id);
                return (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => toggle(opt)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-background-50 transition-colors flex items-center gap-2 ${
                      checked ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-700'
                    }`}
                  >
                    <i
                      className={`${
                        checked
                          ? 'ri-checkbox-fill text-primary-600'
                          : 'ri-checkbox-blank-line text-foreground-300'
                      } text-sm shrink-0`}
                    />
                    <span className="truncate">{opt.label}</span>
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
